import type { FieldValues } from "react-hook-form";
import { extractValue } from "../../../../../libs/utils";

// ---------------------------------------------------------------------------
// Payment plan breakdown snapshot
// ---------------------------------------------------------------------------

export type PaymentPlanBreakdownInputSnapshot = {
  installments: unknown;
  torPeriod: unknown;
  annualRent: unknown;
  rentType: unknown;
  rentValue: unknown;
  unitAreaInSqft: unknown;
  securityDeposit: unknown;
  escalateRentPercent: unknown;
};

export function pickPaymentPlanBreakdownInputSnapshot(params: {
  plan: FieldValues | undefined;
  yearNumber: number;
  hasValidTorType: boolean;
  isTurnoverRentOnly?: boolean;
}): PaymentPlanBreakdownInputSnapshot | null {
  const { plan, yearNumber, hasValidTorType, isTurnoverRentOnly } = params;
  if (!plan) return null;

  const rentType = (plan?.rentType as { value?: string } | string | null)?.["value"] ?? plan?.rentType ?? null;
  const isAreaSqft = rentType === "areaSqft";
  const isFirstYear = yearNumber === 1;

  return {
    installments: isTurnoverRentOnly
      ? null
      : ((plan?.installments as { value?: unknown })?.value ?? plan?.installments ?? null),
    torPeriod: hasValidTorType ? ((plan?.torPeriod as { value?: unknown })?.value ?? plan?.torPeriod ?? null) : null,
    annualRent: isTurnoverRentOnly ? null : (plan?.annualRent ?? null),
    rentType: rentType ?? null,
    rentValue: isAreaSqft ? (plan?.rentValue ?? null) : null,
    unitAreaInSqft: isAreaSqft ? (plan?.unitAreaInSqft ?? null) : null,
    securityDeposit: isFirstYear ? (plan?.securityDeposit ?? null) : null,
    escalateRentPercent: plan?.escalateRentPercent ?? null,
  };
}

// ---------------------------------------------------------------------------
// Service charge breakdown snapshot
// ---------------------------------------------------------------------------

export type ServiceChargeBreakdownInputSnapshot = {
  installments: unknown;
  rentType: unknown;
  annualRent: unknown;
  chargeValue: unknown;
  unitAreaInSqft: unknown;
};

export function pickServiceChargeBreakdownInputSnapshot(
  serviceCharge: FieldValues | undefined | null,
): ServiceChargeBreakdownInputSnapshot | null {
  if (!serviceCharge) return null;

  const rentType =
    (serviceCharge?.rentType as { value?: string } | string | null)?.["value"] ?? serviceCharge?.rentType ?? null;
  const isPerSqFt = rentType === "areaSqft";

  return {
    installments: (serviceCharge?.installments as { value?: unknown })?.value ?? serviceCharge?.installments ?? null,
    rentType: rentType ?? null,
    annualRent: serviceCharge?.annualRent ?? null,
    chargeValue: serviceCharge?.chargeValue ?? null,
    unitAreaInSqft: isPerSqFt ? (serviceCharge?.unitAreaInSqft ?? null) : null,
  };
}

// ---------------------------------------------------------------------------
// Normalize plans for Send-for-Approval comparison
// ---------------------------------------------------------------------------

export type NormalizedPlanSnapshot = {
  noOfYear: number;
  installments: unknown;
  rentType: unknown;
  torPeriod: unknown;
  annualRent: number | null;
  rentValue: number | null;
  unitAreaInSqft: number | null;
  securityDeposit: number | null;
};

export function normalizePlansForSendApprovalSnapshot(
  plans: unknown[] | undefined | null,
  options?: { isTurnoverRentOnly?: boolean },
): NormalizedPlanSnapshot[] {
  if (!Array.isArray(plans) || plans.length === 0) return [];

  const isTurnoverRentOnly = options?.isTurnoverRentOnly ?? false;

  return plans
    .map((raw, idx) => {
      const plan = raw as FieldValues;
      const noOfYear = Number(plan?.noOfYear ?? idx + 1);
      return {
        noOfYear,
        installments: isTurnoverRentOnly ? null : (extractValue(plan?.installments) ?? null),
        rentType: extractValue(plan?.rentType) ?? null,
        torPeriod: extractValue(plan?.torPeriod) ?? null,
        annualRent: isTurnoverRentOnly
          ? null
          : plan?.annualRent === "" || plan?.annualRent == null
            ? null
            : Number(plan.annualRent),
        rentValue: plan?.rentValue === "" || plan?.rentValue == null ? null : Number(plan.rentValue),
        unitAreaInSqft:
          plan?.unitAreaInSqft === "" || plan?.unitAreaInSqft == null ? null : Number(plan.unitAreaInSqft),
        securityDeposit:
          plan?.securityDeposit === "" || plan?.securityDeposit == null ? null : Number(plan.securityDeposit),
      };
    })
    .sort((a, b) => a.noOfYear - b.noOfYear);
}
